if (!customElements.get('hero-video-early-bird')) {
  customElements.define('hero-video-early-bird', class HeroVideoEarlyBird extends HTMLElement {
    connectedCallback() {
      this.previewVideo = this.querySelector('.hero-video-early-bird__preview-video');
      this.modal = this.querySelector('[data-video-modal]');
      this.modalVideo = this.querySelector('.hero-video-early-bird__modal-video');
      this.openButton = this.querySelector('[data-open-video]');
      this.closeButton = this.querySelector('[data-close-video]');
      this.form = this.querySelector('[data-early-bird-form]');
      this.button = this.querySelector('[data-early-bird-submit]');
      this.error = this.querySelector('[data-early-bird-error]');
      this.mainVariantInput = this.querySelector('[data-main-variant]');
      this.quantityInput = this.querySelector('[data-early-bird-quantity]');
      this.quantityDecreaseButton = this.querySelector('[data-quantity-decrease]');
      this.quantityIncreaseButton = this.querySelector('[data-quantity-increase]');
      this.abortController = new AbortController();

      this.openButton?.addEventListener('click', () => this.openVideo());
      this.closeButton?.addEventListener('click', () => this.closeVideo());
      this.modal?.addEventListener('close', () => this.resetModalVideo());
      this.modal?.addEventListener('click', (event) => {
        if (event.target === this.modal) this.closeVideo();
      });
      document.addEventListener('fullscreenchange', () => {
        if (!document.fullscreenElement && this.modal?.open) this.modal.close();
      }, { signal: this.abortController.signal });
      this.modalVideo?.addEventListener('webkitendfullscreen', () => {
        if (this.modal?.open) this.modal.close();
        else this.resetModalVideo();
      });
      this.form?.addEventListener('submit', (event) => this.addBundle(event));
      this.bindQuantityControls();
      this.bindVariantUpdates();
    }

    disconnectedCallback() {
      this.abortController.abort();
      if (this.unsubscribeVariant) this.unsubscribeVariant();
    }

    async openVideo() {
      if (!this.modal || !this.modalVideo) return;
      this.modal.showModal();
      this.previewVideo?.pause();
      this.modalVideo.muted = false;
      this.modalVideo.currentTime = 0;
      this.modalVideo.play().catch(() => {});

      try {
        if (this.modal.requestFullscreen) {
          await this.modal.requestFullscreen();
        } else if (this.modalVideo.webkitEnterFullscreen) {
          this.modalVideo.webkitEnterFullscreen();
        }
      } catch (_) {
        // The dialog remains as a functional fallback when fullscreen is blocked.
      }
    }

    async closeVideo() {
      if (document.fullscreenElement) {
        await document.exitFullscreen().catch(() => {});
      }
      if (this.modal?.open) this.modal.close();
      else this.resetModalVideo();
    }

    resetModalVideo() {
      if (!this.modalVideo) return;
      this.modalVideo.pause();
      this.modalVideo.currentTime = 0;
      this.previewVideo?.play().catch(() => {});
    }

    bindVariantUpdates() {
      if (this.dataset.syncCurrentProduct !== 'true') return;
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.variant?.id) this.mainVariantInput.value = event.data.variant.id;
        });
      }
      document.addEventListener('change', (event) => {
        const input = event.target.closest('product-info input[name="id"]');
        if (input?.value) this.mainVariantInput.value = input.value;
      }, { signal: this.abortController.signal });
    }

    bindQuantityControls() {
      this.quantityDecreaseButton?.addEventListener('click', () => {
        this.setQuantity(this.getQuantity() - 1);
      }, { signal: this.abortController.signal });
      this.quantityIncreaseButton?.addEventListener('click', () => {
        this.setQuantity(this.getQuantity() + 1);
      }, { signal: this.abortController.signal });
      this.quantityInput?.addEventListener('change', () => {
        this.setQuantity(this.quantityInput.value);
      }, { signal: this.abortController.signal });
      window.addEventListener('east-oak:product-quantity-change', (event) => {
        if (event.detail?.source === this) return;
        this.setQuantity(event.detail?.quantity, { silent: true });
      }, { signal: this.abortController.signal });

      const syncedQuantity = Number.parseInt(window.EastOakSyncedProductQuantity, 10);
      this.setQuantity(Number.isFinite(syncedQuantity) ? syncedQuantity : this.getQuantity(), { silent: true });
    }

    getQuantity() {
      const value = Number.parseInt(this.quantityInput?.value, 10);
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    setQuantity(value, options = {}) {
      if (!this.quantityInput) return;
      const quantity = Math.max(1, Number.parseInt(value, 10) || 1);
      this.quantityInput.value = quantity;
      if (this.quantityDecreaseButton) this.quantityDecreaseButton.disabled = quantity <= 1;
      if (!options.silent) this.publishQuantity(quantity);
    }

    publishQuantity(quantity) {
      window.EastOakSyncedProductQuantity = quantity;
      window.dispatchEvent(new CustomEvent('east-oak:product-quantity-change', {
        detail: { quantity, source: this },
      }));
    }

    async addBundle(event) {
      event.preventDefault();
      if (!this.form || this.button?.disabled) return;
      const mainId = Number(this.mainVariantInput?.value);
      const quantity = this.getQuantity();
      const items = [];
      if (mainId) items.push({ id: mainId, quantity });
      if (!mainId) return this.showError('Please select a main product.');

      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.error.hidden = true;
      try {
        const response = await fetch(this.form.action + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this offer.');
        window.location.assign(this.form.dataset.cartUrl);
      } catch (error) {
        this.showError(error.message);
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
      }
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }
  });
}
